import { AreaPageListing, AreaPageListing_ListingType, AreaPageListing_Status } from '@zoocasa/go-search';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { ADDRESS_NOT_AVAILABLE_LABEL, SQFT_NOT_AVAILABLE_LABEL } from 'components/listing-card/listing-card-v2';
import { cleanDashedString } from 'data/addresses';
import { getProviderLogoUrl } from 'utils/compliance';
import generateFullAddress from 'utils/get-full-address';
import { isUsStateCode, provinceOrStateCodeFromString } from 'utils/province_or_state';
import { isQuebecListing } from 'utils/is-quebec-listing';

import type { ListingCardData } from 'components/listing-card/listing-card-v2';

/**
 * View Model for transforming AreaPageListing to ListingCard format
 * 
 * This class adapts the AreaPageListing data structure from go-search to match
 * the ListingCardData interface required by ListingCard component.
 * 
 * Pattern: Follows the same structure as SimilarListingViewModel for consistency.
 * Compliance data is handled externally by transformer functions.
 */
export class AreaPageListingViewModel implements ListingCardData {
  constructor(private listing: AreaPageListing) { }

  get id() {
    return this.listing.id;
  }

  get thumbnailSrc() {
    if (this.hasImageRootStorageKey()) {
      if (this.hasImageSource()) {
        return this.listing.imageRootStorageKey;
      }
      return `https://cdn0.zoocasa.com/${this.listing.imageRootStorageKey}.jpg`;
    }
    return '';
  }

  get thumbnailAlt() {
    // Use the full street address as alt text
    return this.street;
  }

  get displayAddress() {
    return this.listing.displayAddress?.internetAddressDisplay !== undefined ?
      Boolean(this.listing.displayAddress.internetAddressDisplay) : true;
  }

  get href() {
    // Return null for listings without URLs
    const url = this.listing.addressUrlAbsolutePath || this.listing.listingUrlAbsolutePath;
    if (!url) {
      return null;
    }
    
    if (!this.displayAddress) {
      return url;
    }
    
    return cleanDashedString(url);
  }

  get fullAddress() {
    const fullAddress = generateFullAddress(this.street, this.city, this.province, false);
    if (fullAddress) {
      return fullAddress;
    }
    return ADDRESS_NOT_AVAILABLE_LABEL;
  }

  get street() {
    if (!this.displayAddress) {
      return ADDRESS_NOT_AVAILABLE_LABEL;
    }
    return capitalizeWords(this.seoStreet);
  }

  get city() {
    return this.listing.subDivision;
  }

  get province() {
    return this.listing.province;
  }

  get timestamp() {
    // Quebec listings must not display timestamps for regional compliance
    if (isQuebecListing(this.listing.province)) {
      return null;
    }
    
    // Determine the appropriate timestamp based on listing status
    if (this.isSold) {
      return this.listing.soldAt || null;
    } else if (this.isExpired) {
      return this.listing.updatedOn || this.listing.createdAt || null;
    }
    return this.listing.createdAt || null;
  }

  get price() {
    return this.listing.price;
  }

  get soldPrice() {
    return this.listing.SoldPrice > 0 ? this.listing.SoldPrice : null;
  }

  get bedrooms() {
    return this.listing.bedrooms;
  }

  get bathrooms() {
    return this.listing.bathrooms;
  }

  get avgSqft() {
    let avgSqft: number;
    if (this.listing.squareFootage) {
      // gt/gte is the lower bound and lt/lte the upper, matching `sqftLabel` below.
      // Reading them the other way round happens to yield the same number here (the
      // average is symmetric and the fallbacks each return the one bound present),
      // but the names should still say what they hold.
      const { gt, gte, lt, lte } = this.listing.squareFootage;
      const min = gt || gte;
      const max = lt || lte;
      if (min && max) {
        avgSqft = Math.ceil((min + max) / 2);
      } else if (min) {
        avgSqft = min;
      } else if (max) {
        avgSqft = max;
      }
    }
    return avgSqft;
  }

  get sqftLabel() {
    let sqftLabel = '';
    if (this.listing.squareFootage) {
      const { gt, gte, lt, lte } = this.listing.squareFootage;
      const min = gt || gte;
      const max = lt || lte;
      let sqft: string = SQFT_NOT_AVAILABLE_LABEL;

      // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
      sqft = max === 499 ? '0-499' : [min, max].filter(item => item).join('-');

      sqftLabel = !sqft?.trim()?.length ? SQFT_NOT_AVAILABLE_LABEL : sqft.trim();
    }
    return sqftLabel;
  }

  get standardStatus() {
    return this.listing.standardStatus;
  }

  get onMarketDate() {
    return this.listing.onMarketDate ?? null;
  }

  get isRental() {
    return this.listing.listingType === AreaPageListing_ListingType.rent;
  }

  get providerId() {
    return this.listing.providerId;
  }

  get providerLogo() {
    /* At the moment, we only have the provider logo for US listings */
    if (isUsStateCode(provinceOrStateCodeFromString(this.listing?.province))) {
      return getProviderLogoUrl(this.listing.providerId);
    }
    return null;
  }

  get mlsNum() {
    return this.listing.mls;
  }

  get brokerage() {
    return this.listing.brokerage;
  }

  get position() {
    // AreaPageListing doesn't typically include position coordinates
    // This would need to be computed from the address if required
    return null;
  }

  // Helper getters for internal use
  private get isAvailable() {
    return this.listing.statusId === AreaPageListing_Status.Available;
  }

  private get isSold() {
    return this.listing.statusId === AreaPageListing_Status.NotAvailableSold;
  }

  private get isExpired() {
    return !this.isAvailable && !this.isSold;
  }

  private get seoStreet() {
    const unitNumber = this.listing?.unit;
    const streetAddress = this.streetAddress;
    const seoStreet = unitNumber?.length > 0 ? `${unitNumber} - ${streetAddress}` : streetAddress;
    return seoStreet;
  }

  private get streetAddress() {
    const { streetNumber, streetName } = this.listing;
    return streetNumber ? `${streetNumber} ${streetName}` : streetName;
  }

  private hasImageRootStorageKey() {
    return this.listing?.imageRootStorageKey.length > 0;
  }

  private hasImageSource() {
    return this.listing?.imageSource == 'exp';
  }

  toJSON() {
    return {
      id: this.id ?? null,
      thumbnailSrc: this.thumbnailSrc ?? null,
      thumbnailAlt: this.thumbnailAlt ?? null,
      price: this.price ?? null,
      soldPrice: this.soldPrice ?? null,
      timestamp: this.timestamp instanceof Date ? this.timestamp.toISOString() : (this.timestamp ?? null),
      fullAddress: this.fullAddress ?? null,
      street: this.street ?? null,
      city: this.city ?? null,
      province: this.province ?? null,
      href: this.href ?? null,
      mlsNum: this.mlsNum ?? null,
      brokerage: this.brokerage ?? null,
      providerLogo: this.providerLogo ?? null,
      standardStatus: this.standardStatus ?? null,
      onMarketDate: (this.onMarketDate instanceof Date ? this.onMarketDate.toISOString() : this.onMarketDate) ?? null,
      isRental: this.isRental ?? null,
      providerId: this.providerId ?? null,
      bedrooms: this.bedrooms ?? null,
      bathrooms: this.bathrooms ?? null,
      avgSqft: this.avgSqft ?? null,
      sqftLabel: this.sqftLabel ?? null,
      position: this.position ?? null,
    };
  }
}

